/* ──────────────────────  Reports.jsx  ────────────────────── */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import {
  CalendarToday,
  Refresh,
  TrendingUp,
  CurrencyRupee,
  TableRestaurant,
} from '@mui/icons-material';

// === LUXURY THEME ===
const theme = createTheme({
  palette: {
    primary: { main: '#d4af37' },
    secondary: { main: '#b8932b' },
    background: { default: '#ffffff', paper: '#ffffff' },
    text: { primary: '#1a1a1a', secondary: '#555555' },
  },
  typography: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    h4: { fontWeight: 800, color: '#1a1a1a' },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
  },
  shape: { borderRadius: 28 },
});

// === LUXURY CARD ===
const LuxuryCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid rgba(212,175,55,0.3)',
  borderRadius: 28,
  padding: '28px 22px',
  boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 5,
    background: 'linear-gradient(90deg, #d4af37, #b8932b)',
    borderRadius: '28px 28px 0 0',
  },
}));

// === GOLD TEXT ===
const GoldText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #d4af37, #b8932b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 800,
}));

// === MINI CARD ===
const StatMiniCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid rgba(212,175,55,0.25)',
  borderRadius: 24,
  padding: '20px 16px',
  textAlign: 'center',
  boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.1)',
  },
}));

export default function Reports() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const api = process.env.REACT_APP_API_URL;

  // === LEFT: Daily Sales State ===
  const [daily, setDaily] = useState(null);
  const [startDate, setStartDate] = useState(getPastDate(6)); // 7 days ago
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyLoading, setDailyLoading] = useState(false);

  // === RIGHT: Top Items State ===
  const [topItems, setTopItems] = useState([]);
  const [topStartDate, setTopStartDate] = useState(getPastDate(6));
  const [topEndDate, setTopEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [topLoading, setTopLoading] = useState(false);

  function getPastDate(daysAgo) {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().split('T')[0];
  }

  // === Fetch Daily Sales ===
  const fetchDaily = async () => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setDailyLoading(true);
    try {
      const res = await axios.get(
        `${api}/reports-daily-range.php?start=${startDate}&end=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDaily(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        console.error(err);
        alert('Failed to load daily report');
      }
    } finally {
      setDailyLoading(false);
    }
  };

  // === Fetch Top Items ===
  const fetchTopItems = async () => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setTopLoading(true);
    try {
      const res = await axios.get(
        `${api}/reports-top-items-range.php?start=${topStartDate}&end=${topEndDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTopItems(res.data.top_items || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        console.error(err);
        alert('Failed to load top items');
      }
    } finally {
      setTopLoading(false);
    }
  };

  // === Effects ===
  useEffect(() => {
    fetchDaily();
  }, [startDate, endDate]);

  useEffect(() => {
    fetchTopItems();
  }, [topStartDate, topEndDate]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8f8f8', minHeight: '100vh' }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            {/* ───── LEFT: TOTAL SALES REPORT ───── */}
            <Grid item xs={12} md={6}>
              <LuxuryCard>
                <Box sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TrendingUp sx={{ fontSize: 48, color: '#d4af37' }} />
                      <Box>
                        <GoldText variant="h5">Total Sales Report</GoldText>
                        <Typography variant="body2" color="text.secondary">
                          Orders, Revenue & Tables
                        </Typography>
                      </Box>
                    </Stack>
                    <IconButton
                      onClick={fetchDaily}
                      disabled={dailyLoading}
                      sx={{
                        bgcolor: 'rgba(212,175,55,0.15)',
                        '&:hover': { bgcolor: 'rgba(212,175,55,0.25)' },
                      }}
                    >
                      <Refresh
                        sx={{
                          fontSize: 22,
                          color: '#d4af37',
                          animation: dailyLoading ? 'spin 1s linear infinite' : 'none',
                        }}
                      />
                    </IconButton>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Date Range */}
                  <Grid container spacing={2} mb={3}>
                    <Grid item xs={6}>
                      <InputLabel sx={{ mb: 1, color: '#555', fontWeight: 600, fontSize: '0.9rem' }}>
                        Start Date
                      </InputLabel>
                      <TextField
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        fullWidth
                        size="small"
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel sx={{ mb: 1, color: '#555', fontWeight: 600, fontSize: '0.9rem' }}>
                        End Date
                      </InputLabel>
                      <TextField
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        fullWidth
                        size="small"
                        inputProps={{ max: new Date().toISOString().split('T')[0] }}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>
                  </Grid>

                  {/* Stats */}
                  {dailyLoading ? (
                    <Box textAlign="center" py={6}>
                      <CircularProgress color="primary" size={48} />
                    </Box>
                  ) : daily ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <StatMiniCard>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Orders Completed
                          </Typography>
                          <GoldText variant="h4" sx={{ fontSize: '2.4rem' }}>
                            {daily.total_orders || 0}
                          </GoldText>
                        </StatMiniCard>
                      </Grid>
                      <Grid item xs={12}>
                        <StatMiniCard>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Total Revenue
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{
                              fontSize: '2.4rem',
                              fontWeight: 800,
                              color: '#006600',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                            }}
                          >
                            <CurrencyRupee sx={{ fontSize: '0.8em' }} />
                            {Number(daily.total_revenue || 0).toFixed(2)}
                          </Typography>
                        </StatMiniCard>
                      </Grid>
                      <Grid item xs={12}>
                        <StatMiniCard>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Tables Served
                          </Typography>
                          <Typography variant="h4" sx={{ fontSize: '2.4rem', fontWeight: 800, color: '#1b5e20' }}>
                            {daily.total_tables_served || 0}
                          </Typography>
                        </StatMiniCard>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography textAlign="center" color="#999" py={4}>
                      No data available
                    </Typography>
                  )}
                </Box>
              </LuxuryCard>
            </Grid>

            {/* ───── RIGHT: TOP SELLING ITEMS ───── */}
            <Grid item xs={12} md={6}>
              <LuxuryCard>
                <Box sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TableRestaurant sx={{ fontSize: 48, color: '#d4af37' }} />
                      <Box>
                        <GoldText variant="h5">Top Selling Items</GoldText>
                        <Typography variant="body2" color="text.secondary">
                          Most popular dishes
                        </Typography>
                      </Box>
                    </Stack>
                    <IconButton
                      onClick={fetchTopItems}
                      disabled={topLoading}
                      sx={{
                        bgcolor: 'rgba(212,175,55,0.15)',
                        '&:hover': { bgcolor: 'rgba(212,175,55,0.25)' },
                      }}
                    >
                      <Refresh
                        sx={{
                          fontSize: 22,
                          color: '#d4af37',
                          animation: topLoading ? 'spin 1s linear infinite' : 'none',
                        }}
                      />
                    </IconButton>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Date Range */}
                  <Grid container spacing={2} mb={3}>
                    <Grid item xs={6}>
                      <InputLabel sx={{ mb: 1, color: '#555', fontWeight: 600, fontSize: '0.9rem' }}>
                        Start Date
                      </InputLabel>
                      <TextField
                        type="date"
                        value={topStartDate}
                        onChange={(e) => setTopStartDate(e.target.value)}
                        fullWidth
                        size="small"
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel sx={{ mb: 1, color: '#555', fontWeight: 600, fontSize: '0.9rem' }}>
                        End Date
                      </InputLabel>
                      <TextField
                        type="date"
                        value={topEndDate}
                        onChange={(e) => setTopEndDate(e.target.value)}
                        fullWidth
                        size="small"
                        inputProps={{ max: new Date().toISOString().split('T')[0] }}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>
                  </Grid>

                  {/* Table */}
                  <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid rgba(212,175,55,0.2)'}}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 700, color: '#1a1a1a', py: 1.5 }}>Item</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#1a1a1a', py: 1.5 }}>
                            Qty
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#1a1a1a', py: 1.5 }}>
                            Revenue
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topLoading ? (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                              <CircularProgress size={28} color="primary" />
                            </TableCell>
                          </TableRow>
                        ) : topItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ textAlign: 'center', py: 5, color: '#999' }}>
                              No sales in this period
                            </TableCell>
                          </TableRow>
                        ) : (
                          topItems.slice(0, 8).map((item, i) => (
                            <TableRow
                              key={i}
                              sx={{
                                '&:hover': { bgcolor: '#fffbeb' },
                                '&:last-child td': { border: 0 },
                              }}
                            >
                              <TableCell sx={{ py: 1.5, fontWeight: 500, fontSize: '0.9rem' }}>
                                {item.name}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, color: '#d4af37' }}>
                                {item.quantity_sold}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, color: '#006600' }}>
                                ₹{Number(item.revenue_generated).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Box>
              </LuxuryCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Spin Animation */}
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </ThemeProvider>
  );
}